import { canAccessAgencyPath } from './agencyAccess';
import type { ShigoUser } from './model';
import { isNetworkFeatureAllowed, NetworkAvailableFeature } from './networkFeatures';
import {
  canAccessAssignedPublisher,
  resolveOwnedDashboardContext,
  ShigoDashboardContext,
} from './dashboardDestination';
import {
  hasAllShigoPermissions,
  hasAnyShigoPermission,
  hasShigoPermission,
  ShigoPermission,
  ShigoRole,
} from './permissions';
import { resolvePublisherAccessRequirement } from './publisherAccessPolicy';
import type { AccessRequirement, ShigoAccessibleRoute } from './accessPolicy.types';
import { accessibleRoutes, publicRoutes } from './accessPolicy.routes';

export type { ShigoAccessibleRoute } from './accessPolicy.types';

const campaignsReadPermissions = [
  ShigoPermission.MadBuyCampaignRead,
  ShigoPermission.MadBuySupplyRead,
  ShigoPermission.MadBuyCreativesRead,
] as const;

export function canAccessShigoPath(permissions: readonly string[], pathname: string) {
  if (publicRoutes.has(pathname)) return true;

  const requirement = resolveAccessRequirement(pathname);
  if (!requirement) return false;
  if ('permission' in requirement) {
    return hasShigoPermission(permissions, requirement.permission);
  }
  if ('allOf' in requirement) {
    return (
      hasAllShigoPermissions(permissions, requirement.allOf) &&
      (!requirement.anyOf || hasAnyShigoPermission(permissions, requirement.anyOf))
    );
  }
  if ('allOfAny' in requirement) {
    return requirement.allOfAny.every((permissionGroup) =>
      hasAnyShigoPermission(permissions, permissionGroup),
    );
  }
  return hasAnyShigoPermission(permissions, requirement.anyOf);
}

export function canReadIntegrationJourney(user: ShigoUser) {
  return (
    !user.roles.includes(ShigoRole.SalesRepresentative) &&
    user.roles.some((role) =>
      [
        ShigoRole.Admin,
        ShigoRole.AdminReadOnly,
        ShigoRole.User,
        ShigoRole.UserReadOnly,
        ShigoRole.Publisher,
        ShigoRole.SuperAdmin,
      ].includes(role as ShigoRole),
    )
  );
}

export function canAccessShigoRoute(user: ShigoUser, pathname: string) {
  if (pathname.startsWith('/a/')) return canAccessAgencyPath(user, pathname);
  if (
    /^\/demand\/integrations\/[^/]+\/demand\/[^/]+\/details\/journey\/?$/.test(pathname) &&
    !canReadIntegrationJourney(user)
  )
    return false;
  return (
    canAccessOwnedDashboardPath(user, pathname) &&
    canAccessShigoPath(user.permissions, pathname) &&
    canAccessPathNetworkFeature(user, pathname) &&
    canAccessAssignedPublisher(user, pathname)
  );
}

export function canAccessPathNetworkFeature(user: ShigoUser, pathname: string) {
  const requiredFeature = resolveNetworkFeatureRequirement(pathname);
  return requiredFeature ? isNetworkFeatureAllowed(user, requiredFeature) : true;
}

export function canAccessOwnedDashboardPath(user: ShigoUser, pathname: string) {
  if (pathname.startsWith('/a/')) return canAccessAgencyPath(user, pathname);
  if (publicRoutes.has(pathname)) return true;

  const context = resolveOwnedDashboardContext(user);
  if (pathname === '/reporting/network-performance' || pathname === '/admin/stitcher-access') {
    return context === ShigoDashboardContext.SuperAdmin;
  }
  if (context === ShigoDashboardContext.SuperAdmin) {
    return (
      pathname === '/' ||
      pathname === '/networks' ||
      pathname.startsWith('/networks/') ||
      pathname === '/admin' ||
      pathname === '/admin/users' ||
      pathname === '/reporting' ||
      pathname === '/reporting/report-explorer' ||
      (/^\/p\/[^/]+(?:\/reporting\/report-explorer)?$/.test(pathname) &&
        canAccessAssignedPublisher(user, pathname))
    );
  }
  if (context === ShigoDashboardContext.Publisher) {
    return /^\/p\/[^/]+(?:\/reporting\/report-explorer)?$/.test(pathname);
  }
  return context === ShigoDashboardContext.Main;
}

export function resolveDeniedRoute(
  permissions: readonly string[],
  pathname: string,
): ShigoAccessibleRoute | null {
  const sectionFallback = sectionFallbackForPath(pathname);
  if (sectionFallback && canAccessShigoPath(permissions, sectionFallback)) return sectionFallback;

  return accessibleRoutes.find((route) => canAccessShigoPath(permissions, route)) ?? null;
}

export function resolveDeniedRouteForUser(
  user: ShigoUser,
  pathname: string,
): ShigoAccessibleRoute | null {
  const sectionFallback = sectionFallbackForPath(pathname);
  if (sectionFallback && canAccessShigoRoute(user, sectionFallback)) return sectionFallback;

  return accessibleRoutes.find((route) => canAccessShigoRoute(user, route)) ?? null;
}

function resolveAccessRequirement(pathname: string): AccessRequirement | null {
  // Placeholder access until the Agency permission contract is available.
  if (pathname === '/campaigns/agencies') return { permission: ShigoPermission.SupplyRead };
  if (pathname === '/campaigns/supply/tailored-supply/create') {
    return { permission: ShigoPermission.MadBuySupplyWrite };
  }
  if (pathname.startsWith('/campaigns/supply/tailored-supply/edit/')) {
    return { permission: ShigoPermission.MadBuySupplyWrite };
  }
  if (pathname.endsWith('/line-items/create') || pathname.includes('/line-items/edit/')) {
    return { permission: ShigoPermission.MadBuyCampaignWrite };
  }
  if (pathname === '/targeting/new') return { permission: ShigoPermission.TargetingCreate };
  if (pathname.startsWith('/targeting/') && pathname.endsWith('/edit')) {
    return { permission: ShigoPermission.TargetingEdit };
  }
  if (pathname === '/') return { permission: ShigoPermission.HomeRead };
  if (pathname === '/networks') return { permission: ShigoPermission.All };
  if (/^\/networks\/[^/]+\/details(?:\/overview)?$/.test(pathname)) {
    return { permission: ShigoPermission.All };
  }
  if (pathname === '/networks/create') return { permission: ShigoPermission.NetworkCreate };
  if (pathname.startsWith('/networks/edit/')) {
    return { permission: ShigoPermission.NetworkEdit };
  }
  if (/^\/p\/[^/]+$/.test(pathname)) {
    return {
      allOf: [ShigoPermission.PublisherDashboardRead, ShigoPermission.PublisherDashboardHomeRead],
    };
  }
  if (/^\/p\/[^/]+\/reporting\/report-explorer$/.test(pathname)) {
    return {
      allOf: [
        ShigoPermission.PublisherDashboardRead,
        ShigoPermission.PublisherDashboardReportingRead,
      ],
    };
  }
  if (pathname === '/campaigns') return { anyOf: campaignsReadPermissions };
  if (
    pathname === '/campaigns/brands' ||
    /^\/campaigns\/(?:brands|agencies)\/[^/]+\/details$/.test(pathname)
  ) {
    return { permission: ShigoPermission.MadBuyCampaignRead };
  }
  if (pathname.startsWith('/campaigns/supply/')) {
    return { permission: ShigoPermission.MadBuySupplyRead };
  }
  if (
    pathname === '/campaigns/campaigns' ||
    pathname === '/campaigns/channel-groups' ||
    pathname.startsWith('/campaigns/campaigns/')
  ) {
    return { permission: ShigoPermission.MadBuyCampaignRead };
  }
  if (pathname.startsWith('/campaigns/creatives')) {
    return { permission: ShigoPermission.MadBuyCreativesRead };
  }
  if (pathname === '/targeting' || pathname.startsWith('/targeting/')) {
    return { permission: ShigoPermission.TargetingRead };
  }
  if (pathname === '/admin') {
    return {
      anyOf: [ShigoPermission.AdminUsersRead, ShigoPermission.AdminChangelogRead],
    };
  }
  if (pathname === '/admin/users' || pathname === '/admin/stitcher-access') {
    return { permission: ShigoPermission.AdminUsersRead };
  }
  if (pathname === '/admin/changelog') {
    return { permission: ShigoPermission.AdminChangelogRead };
  }
  if (pathname === '/a2v/network-template') {
    return {
      allOf: [ShigoPermission.A2vRead, ShigoPermission.A2vNetworkTemplateRead],
    };
  }
  if (pathname === '/a2v/assets') {
    return { allOf: [ShigoPermission.A2vRead, ShigoPermission.A2vAssetsRead] };
  }
  if (pathname === '/a2v') return { permission: ShigoPermission.A2vRead };
  if (pathname === '/design-system') return { permission: ShigoPermission.All };
  if (pathname === '/supply/placements/create/standard') {
    return {
      allOf: [ShigoPermission.SupplyPlacementsRead, ShigoPermission.SupplyPlacementsCreate],
    };
  }
  if (pathname === '/supply/placements/create/ad-unit') {
    return {
      allOf: [ShigoPermission.SupplyPlacementsRead, ShigoPermission.SupplyPlacementsCreate],
    };
  }
  if (pathname.startsWith('/supply/placements/edit/')) {
    return {
      allOf: [ShigoPermission.SupplyPlacementsRead],
      anyOf: [ShigoPermission.SupplyPlacementsUpdate, ShigoPermission.SupplyPlacementsEdit],
    };
  }
  if (pathname === '/supply/placements') {
    return { permission: ShigoPermission.SupplyPlacementsRead };
  }
  if (pathname === '/supply/lists/rewrite/create' || pathname === '/supply/lists/schain/create') {
    return {
      allOfAny: [
        [ShigoPermission.SupplyListsRead, ShigoPermission.SupplyAppsRead],
        [ShigoPermission.SupplyListsCreate, ShigoPermission.SupplyWrite],
      ],
    };
  }
  if (
    pathname.startsWith('/supply/lists/rewrite/edit/') ||
    pathname.startsWith('/supply/lists/schain/edit/')
  ) {
    return {
      allOfAny: [
        [ShigoPermission.SupplyListsRead, ShigoPermission.SupplyAppsRead],
        [ShigoPermission.SupplyListsEdit, ShigoPermission.SupplyWrite],
      ],
    };
  }
  if (pathname === '/supply/lists' || pathname.startsWith('/supply/lists/')) {
    return {
      anyOf: [ShigoPermission.SupplyListsRead, ShigoPermission.SupplyAppsRead],
    };
  }
  if (pathname === '/supply/domains' || pathname.startsWith('/supply/domains/')) {
    return { permission: ShigoPermission.DemandComplianceRead };
  }
  if (pathname === '/supply/blocked-list' || pathname.startsWith('/supply/blocked-list/')) {
    return { permission: ShigoPermission.BlocklistCreativesRead };
  }
  if (pathname.startsWith('/supply/apps/edit/')) {
    return {
      allOf: [
        ShigoPermission.SupplyAppsRead,
        ShigoPermission.SupplyAppsInventoryRead,
        ShigoPermission.SupplyAppsInventoryEdit,
      ],
    };
  }
  if (pathname === '/supply/apps/requests' || pathname.startsWith('/supply/apps/requests/')) {
    return {
      allOf: [ShigoPermission.SupplyAppsRead, ShigoPermission.SupplyAppRequestsRead],
    };
  }
  if (pathname === '/supply/apps' || pathname === '/supply/apps/') {
    return {
      allOf: [ShigoPermission.SupplyAppsRead, ShigoPermission.SupplyAppsInventoryRead],
    };
  }
  if (pathname === '/supply/endpoint-generator') {
    return {
      anyOf: [
        ShigoPermission.SupplyEndpointGeneratorRead,
        ShigoPermission.SupplyPublishersEndpointGeneratorGenerate,
      ],
    };
  }
  if (pathname === '/supply/ads-txt' || pathname === '/supply/ads-txt/') {
    return {
      allOf: [ShigoPermission.SupplyAdsTxtRead],
      anyOf: [
        ShigoPermission.SupplyAdsTxtAppsRead,
        ShigoPermission.SupplyAdsTxtRecordsRead,
        ShigoPermission.SupplyAdsTxtDomainsRead,
      ],
    };
  }
  if (pathname === '/supply/ads-txt/records') {
    return {
      allOf: [ShigoPermission.SupplyAdsTxtRead, ShigoPermission.SupplyAdsTxtRecordsRead],
    };
  }
  if (pathname === '/supply/ads-txt/apps') {
    return { allOf: [ShigoPermission.SupplyAdsTxtRead, ShigoPermission.SupplyAdsTxtAppsRead] };
  }
  if (pathname === '/supply/ads-txt/domains') {
    return {
      allOf: [ShigoPermission.SupplyAdsTxtRead, ShigoPermission.SupplyAdsTxtDomainsRead],
    };
  }
  if (pathname === '/supply/tailored-supply/create') {
    return { permission: ShigoPermission.TargetingEdit };
  }
  if (pathname === '/supply/tailored-supply' || pathname === '/supply/tailored-supply/') {
    return { permission: ShigoPermission.TargetingRead };
  }
  const publisherRequirement = resolvePublisherAccessRequirement(pathname);
  if (publisherRequirement) return publisherRequirement;
  if (pathname === '/supply' || pathname.startsWith('/supply/')) {
    return { permission: ShigoPermission.SupplyRead };
  }
  if (pathname === '/demand/integrations/create') {
    return {
      allOf: [ShigoPermission.DemandRead, ShigoPermission.DemandIntegrationsCreate],
    };
  }
  if (pathname.startsWith('/demand/integrations/edit/')) {
    return {
      allOf: [ShigoPermission.DemandRead, ShigoPermission.DemandIntegrationsEdit],
    };
  }
  if (/^\/demand\/integrations\/[^/]+\/demand\/[^/]+\/details\/placements$/.test(pathname)) {
    return {
      allOf: [
        ShigoPermission.DemandRead,
        ShigoPermission.DemandIntegrationsRead,
        ShigoPermission.DemandIntegrationPlacementsRead,
      ],
    };
  }
  if (/^\/demand\/integrations\/[^/]+\/demand\/[^/]+\/details(?:\/.*)?$/.test(pathname)) {
    return {
      allOf: [ShigoPermission.DemandRead, ShigoPermission.DemandIntegrationsRead],
    };
  }
  if (pathname === '/demand/integrations') {
    return {
      allOf: [ShigoPermission.DemandRead, ShigoPermission.DemandIntegrationsRead],
    };
  }
  if (pathname === '/demand/deals/create') {
    return {
      allOf: [
        ShigoPermission.DemandRead,
        ShigoPermission.DemandDealsRead,
        ShigoPermission.DemandDealsCreate,
      ],
    };
  }
  if (pathname.startsWith('/demand/deals/edit/')) {
    return {
      allOf: [
        ShigoPermission.DemandRead,
        ShigoPermission.DemandDealsRead,
        ShigoPermission.DemandDealsEdit,
      ],
    };
  }
  if (pathname === '/demand/deals' || pathname === '/demand/deals/') {
    return {
      allOf: [ShigoPermission.DemandRead, ShigoPermission.DemandDealsRead],
    };
  }
  if (pathname === '/demand/owners/create') {
    return {
      allOf: [ShigoPermission.DemandRead, ShigoPermission.DemandPartnersCreate],
    };
  }
  if (pathname.startsWith('/demand/owners/edit/')) {
    return {
      allOf: [ShigoPermission.DemandRead, ShigoPermission.DemandPartnersEdit],
    };
  }
  if (pathname === '/demand/owners' || pathname === '/demand/owners/') {
    return {
      allOf: [ShigoPermission.DemandRead, ShigoPermission.DemandPartnersRead],
    };
  }
  if (pathname === '/demand/partners/create') {
    return {
      allOf: [ShigoPermission.DemandRead, ShigoPermission.DemandPartnersCreate],
    };
  }
  if (pathname.startsWith('/demand/partners/edit/')) {
    return {
      allOf: [ShigoPermission.DemandRead, ShigoPermission.DemandPartnersEdit],
    };
  }
  if (
    pathname === '/demand/partners' ||
    /^\/demand\/partners\/[^/]+\/details(?:\/overview)?$/.test(pathname)
  ) {
    return {
      allOf: [ShigoPermission.DemandRead, ShigoPermission.DemandPartnersRead],
    };
  }
  if (pathname === '/demand') {
    return {
      allOf: [ShigoPermission.DemandRead],
      anyOf: [
        ShigoPermission.DemandPartnersRead,
        ShigoPermission.DemandIntegrationsRead,
        ShigoPermission.DemandDealsRead,
      ],
    };
  }
  if (pathname === '/debug') return { permission: ShigoPermission.DebugRead };
  if (pathname === '/reporting/saved-reports' || pathname === '/reporting/saved-reports/shared') {
    return { permission: ShigoPermission.ReportingSavedReportsRead };
  }
  if (pathname === '/reporting/network-performance') {
    return { permission: ShigoPermission.ReportingReportBuilderRead };
  }
  if (pathname === '/reporting/performance-analyzer') {
    return { permission: ShigoPermission.ReportingPerformanceRead };
  }
  if (pathname === '/reporting' || pathname === '/reporting/report-explorer') {
    return { permission: ShigoPermission.ReportingReportBuilderRead };
  }
  return null;
}

function sectionFallbackForPath(pathname: string): ShigoAccessibleRoute | null {
  if (pathname.startsWith('/networks/')) return '/networks';
  if (pathname.startsWith('/campaigns/supply/')) return '/campaigns/supply/apps';
  if (pathname.startsWith('/campaigns/campaigns/')) return '/campaigns/campaigns';
  if (pathname.startsWith('/targeting/')) return '/targeting';
  if (pathname.startsWith('/admin/')) return '/admin/users';
  if (pathname.startsWith('/a2v/')) return '/a2v';
  if (pathname.startsWith('/supply/placements/')) return '/supply/placements';
  if (pathname.startsWith('/supply/publishers/')) return '/supply/publishers';
  if (pathname.startsWith('/supply/lists/')) return '/supply/lists/all';
  if (pathname.startsWith('/supply/blocked-list/')) {
    return '/supply/blocked-list/creatives';
  }
  if (pathname.startsWith('/supply/apps/edit/')) return '/supply/apps';
  if (pathname === '/supply/apps/requests' || pathname.startsWith('/supply/apps/requests/')) {
    return '/supply/apps';
  }
  if (pathname.startsWith('/supply/domains/')) return '/supply/domains';
  if (pathname.startsWith('/supply/tailored-supply/')) return '/supply/tailored-supply';
  if (pathname.startsWith('/supply/endpoint-generator')) return '/supply';
  if (pathname.startsWith('/supply/ads-txt')) return '/supply';
  if (pathname.startsWith('/supply/')) return '/supply';
  if (pathname.startsWith('/demand/owners/')) return '/demand/owners';
  if (pathname.startsWith('/demand/')) return '/demand/partners';
  if (pathname.startsWith('/reporting/saved-reports')) return '/reporting/report-explorer';
  if (pathname.startsWith('/reporting/')) return '/reporting/report-explorer';
  return null;
}

function resolveNetworkFeatureRequirement(pathname: string): NetworkAvailableFeature | null {
  if (pathname === '/demand/owners' || pathname.startsWith('/demand/owners/')) {
    return NetworkAvailableFeature.Owner;
  }
  if (pathname === '/supply/placements/create/ad-unit') {
    return NetworkAvailableFeature.AdUnit;
  }
  if (pathname === '/campaigns' || pathname.startsWith('/campaigns/')) {
    return NetworkAvailableFeature.Campaign;
  }
  if (pathname === '/a2v' || pathname.startsWith('/a2v/')) {
    return NetworkAvailableFeature.A2v;
  }
  if (/^\/supply\/publishers\/[^/]+\/details\/a2v$/.test(pathname)) {
    return NetworkAvailableFeature.A2v;
  }
  return null;
}
